import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { IEditorPackage } from './types';

// Search icon
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Close/X icon
const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Package icon
const PackageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
  </svg>
);

interface INpmPackage {
  name: string;
  version: string;
  description: string;
}

interface IPackagesPanelProps {
  packages: IEditorPackage[];
  onPackagesChange: (packages: IEditorPackage[]) => void;
}

const PackagesPanel = ({ packages, onPackagesChange }: IPackagesPanelProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<INpmPackage[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search npm packages using npms.io API
  const searchPackages = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.npms.io/v2/search?q=${encodeURIComponent(query)}&size=8`
      );
      const data = await response.json();
      const results: INpmPackage[] = data.results.map((result: any) => ({
        name: result.package.name,
        version: result.package.version,
        description: result.package.description || '',
      }));
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce(searchPackages, 300),
    [searchPackages]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddPackage = (pkg: INpmPackage) => {
    // Check if already installed
    if (packages.some((p) => p.name === pkg.name)) {
      return;
    }
    onPackagesChange([...packages, { name: pkg.name, version: pkg.version }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleRemovePackage = (name: string) => {
    onPackagesChange(packages.filter((p) => p.name !== name));
  };

  const isPackageInstalled = (name: string) => packages.some((p) => p.name === name);

  return (
    <VStack align="stretch" spacing={3}>
      {/* Search input */}
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          {isSearching ? (
            <Spinner size="xs" color="whiteAlpha.500" />
          ) : (
            <Box color="whiteAlpha.500">
              <SearchIcon />
            </Box>
          )}
        </InputLeftElement>
        <Input
          placeholder="Search npm packages..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          bg="whiteAlpha.100"
          borderColor="whiteAlpha.200"
          _hover={{ borderColor: 'whiteAlpha.300' }}
          _focus={{ borderColor: 'purple.400', boxShadow: 'none' }}
          _placeholder={{ color: 'whiteAlpha.400' }}
        />
      </InputGroup>

      {/* Search results */}
      {searchResults.length > 0 && (
        <VStack
          align="stretch"
          spacing={1}
          maxH="150px"
          overflowY="auto"
          bg="whiteAlpha.50"
          borderRadius="md"
          p={1}
        >
          {searchResults.map((pkg) => (
            <HStack
              key={pkg.name}
              justify="space-between"
              p={2}
              borderRadius="md"
              _hover={{ bg: 'whiteAlpha.100' }}
              cursor="pointer"
              onClick={() => !isPackageInstalled(pkg.name) && handleAddPackage(pkg)}
              opacity={isPackageInstalled(pkg.name) ? 0.5 : 1}
            >
              <VStack align="start" spacing={0} flex={1} minW={0}>
                <HStack spacing={2}>
                  <Text fontSize="xs" fontWeight="600" color="white" isTruncated>
                    {pkg.name}
                  </Text>
                  <Text fontSize="10px" color="whiteAlpha.500">
                    {pkg.version}
                  </Text>
                </HStack>
                <Text fontSize="10px" color="whiteAlpha.500" isTruncated maxW="100%">
                  {pkg.description}
                </Text>
              </VStack>
              {isPackageInstalled(pkg.name) ? (
                <Text fontSize="10px" color="green.400">
                  Added
                </Text>
              ) : (
                <Button size="xs" colorScheme="purple" variant="ghost">
                  Add
                </Button>
              )}
            </HStack>
          ))}
        </VStack>
      )}

      {/* Installed packages */}
      {packages.length > 0 && (
        <VStack align="stretch" spacing={1}>
          <Text fontSize="10px" color="whiteAlpha.400" fontWeight="600" textTransform="uppercase">
            Installed ({packages.length})
          </Text>
          {packages.map((pkg) => (
            <HStack
              key={pkg.name}
              justify="space-between"
              p={2}
              bg="whiteAlpha.50"
              borderRadius="md"
            >
              <HStack spacing={2}>
                <Box color="purple.300">
                  <PackageIcon />
                </Box>
                <Text fontSize="xs" color="white">
                  {pkg.name}
                </Text>
                <Text fontSize="10px" color="whiteAlpha.500">
                  @{pkg.version}
                </Text>
              </HStack>
              <IconButton
                aria-label="Remove package"
                icon={<CloseIcon />}
                size="xs"
                variant="ghost"
                color="whiteAlpha.500"
                _hover={{ color: 'red.400', bg: 'whiteAlpha.100' }}
                onClick={() => handleRemovePackage(pkg.name)}
              />
            </HStack>
          ))}
        </VStack>
      )}

      {/* Empty state */}
      {packages.length === 0 && searchResults.length === 0 && !searchQuery && (
        <Text fontSize="xs" color="whiteAlpha.400" textAlign="center" py={2}>
          Search and add npm packages to use in your code via ES modules
        </Text>
      )}
    </VStack>
  );
};

export default PackagesPanel;
